export function isSlaActive(slaContent) {
  let currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  let startsAt = null;
  if (slaContent.startsAt) {
    startsAt = new Date(slaContent.startsAt);
    startsAt.setHours(0,0,0,0);
  }

  let endsAt = null;
  if (slaContent.endsAt) {
    endsAt = new Date(slaContent.endsAt);
    endsAt.setHours(0,0,0,0);
  }

  if (startsAt === null && endsAt === null) {
    return false;
  } else if (startsAt !== null && endsAt === null) {
    return startsAt <= currentDate;
  } else if (startsAt === null && endsAt !== null) {
    return currentDate <= endsAt;
  } else {
    return startsAt <= currentDate && currentDate <= endsAt;
  }
};

// Mirrors SlaStatusHelper.IsActive on the server.
export function isPeriodActive(start, end) {
  return isSlaActive({ startsAt: start, endsAt: end });
}

function atMidnight(date) {
  if (!date) return null;

  const value = new Date(date);
  value.setHours(0, 0, 0, 0);

  return value;
}

// Mirrors SlaStatusHelper.IsPending on the server: a single period has not begun yet, because its
// start is in the future. A missing start means "has always been running", which is never pending.
// Judged per period - merging the two periods' end dates here would let an already-expired period
// mask an open-ended one that has not started yet.
function isPending(start, end) {
  const startDate = atMidnight(start);

  if (!startDate) return false;

  const currentDate = atMidnight(new Date());
  const endDate = atMidnight(end);

  return startDate > currentDate && (!endDate || endDate >= startDate);
}

// Mirrors SlaStatusHelper.Resolve on the server. Keep the two in step.
export function resolveSlaStatus(slaContent) {
  if (!slaContent) return "None";

  const { startsAt, endsAt, extendedStartsAt, extendedEndsAt } = slaContent;

  if (isPeriodActive(startsAt, endsAt)) return "Active";
  if (isPeriodActive(extendedStartsAt, extendedEndsAt)) return "Extended";
  if (isPending(startsAt, endsAt) || isPending(extendedStartsAt, extendedEndsAt))
    return "Pending";
  if (startsAt || endsAt || extendedStartsAt || extendedEndsAt) return "Expired";

  return "None";
}

function latestOf(a, b) {
  const dates = [a, b].map(atMidnight).filter(Boolean);

  return dates.length ? new Date(Math.max(...dates)) : null;
}

function earliestOf(a, b) {
  const dates = [a, b].map(atMidnight).filter(Boolean);

  return dates.length ? new Date(Math.min(...dates)) : null;
}

// Mirrors SlaStatusHelper.StatusDate on the server: the date the badge shows alongside the status,
// which period it comes from depends on the status. Keep the two in step.
export function slaStatusDate(slaContent) {
  if (!slaContent) return null;

  const { startsAt, endsAt, extendedStartsAt, extendedEndsAt } = slaContent;

  switch (resolveSlaStatus(slaContent)) {
    case "Active":
      return endsAt ? atMidnight(endsAt) : null;
    case "Extended":
      return extendedEndsAt ? atMidnight(extendedEndsAt) : null;
    case "Pending":
      return earliestOf(
        isPending(startsAt, endsAt) ? startsAt : null,
        isPending(extendedStartsAt, extendedEndsAt) ? extendedStartsAt : null
      );
    case "Expired":
      return latestOf(endsAt, extendedEndsAt);
    default:
      return null;
  }
}

// Full literal class strings - Tailwind 2 purge drops anything it cannot see in the source.
const STATUS_COLORS = {
  Active: "text-indicator-green",
  Extended: "text-prime-blue",
  Expired: "text-prime-red-dark",
  Pending: "text-prime-grey-darkest",
  None: "text-prime-grey-darkest",
};

export function slaStatusColor(status) {
  return STATUS_COLORS[status] ?? STATUS_COLORS.None;
}

const STATUS_TRANSLATION_KEYS = {
  Active: "statusActive",
  Extended: "statusExtended",
  Expired: "statusExpired",
  // Pending has its own wording because its date is the *start* of cover, not an expiry.
  Pending: "statusPending",
  None: "statusNone",
};

export function slaStatusTranslationKey(status) {
  return STATUS_TRANSLATION_KEYS[status] ?? STATUS_TRANSLATION_KEYS.None;
}
